import React, { useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../../shared/ui/FormField';
import { DollarSignIcon, InfoIcon, LinkIcon, FormInputIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, CalendarIcon, ExternalLinkIcon, PlusIcon, ShareIcon, SettingsIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { CaptureField, CaptureFieldsEditor } from '../../../shared/ui/CaptureFieldsEditor';
import { ShareLinkModal } from './ShareLinkModal';
interface StaticLink {
  id: string;
  name: string;
  reference: string;
  amount: string;
  currency: string;
  url: string;
  active: boolean;
  createdAt: string;
  usageCount: number;
  totalCollected: number;
  expiryDate?: string;
}
interface FixedLinksPaymentProps {
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function FixedLinksPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: FixedLinksPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [captureFields, setCaptureFields] = useState<CaptureField[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentLinkToShare, setCurrentLinkToShare] = useState<StaticLink | null>(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('23:59');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [existingLinks, setExistingLinks] = useState<StaticLink[]>([{
    id: '1',
    name: 'Monthly Subscription',
    reference: 'monthly-sub',
    amount: '19.99',
    currency: getTenant().currency.code,
    url: 'https://payment.example.com/pay/monthly-sub',
    active: true,
    createdAt: '2023-05-15',
    usageCount: 47,
    totalCollected: 939.53,
    expiryDate: '2023-12-31 23:59'
  }, {
    id: '2',
    name: 'Premium Plan',
    reference: 'premium-plan',
    amount: '49.99',
    currency: getTenant().currency.code,
    url: 'https://payment.example.com/pay/premium-plan',
    active: true,
    createdAt: '2023-06-22',
    usageCount: 23,
    totalCollected: 1149.77
  }, {
    id: '3',
    name: 'One-time Donation',
    reference: 'donation-form',
    amount: '10.00',
    currency: getTenant().currency.code,
    url: 'https://payment.example.com/pay/donation-form',
    active: false,
    createdAt: '2023-08-10',
    usageCount: 156,
    totalCollected: 1560.0,
    expiryDate: '2023-10-15 18:00'
  }]);
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const toggleLinkStatus = (id: string) => {
    setExistingLinks(existingLinks.map(link => link.id === id ? {
      ...link,
      active: !link.active
    } : link));
  };
  const deleteLink = (id: string) => {
    if (confirm('Are you sure you want to permanently delete this payment link?')) {
      setExistingLinks(existingLinks.filter(link => link.id !== id));
    }
  };
  const copyLinkUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      console.log('Link copied to clipboard');
    });
  };
  const handleShareLink = (link: StaticLink) => {
    setCurrentLinkToShare(link);
    setShowShareModal(true);
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = expiryDate ? parseDate(expiryDate) : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() - 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() + 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    if (date) {
      setExpiryDate(formatDate(date));
      setShowDatePicker(false);
    }
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handleCreateLink = () => {
    let expiryDateTimeString = undefined;
    if (hasExpiry && expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => part.trim());
      expiryDateTimeString = `${year}-${month}-${day} ${expiryTime}`;
    }
    const newLink: StaticLink = {
      id: `link-${Date.now()}`,
      name: description || 'New Payment Link',
      reference: reference || `link-${Date.now()}`,
      amount: amount || '0.00',
      currency: currency,
      url: `https://payment.example.com/pay/${reference || Date.now()}`,
      active: true,
      createdAt: new Date().toISOString().split('T')[0],
      usageCount: 0,
      totalCollected: 0,
      expiryDate: expiryDateTimeString
    };
    setExistingLinks([newLink, ...existingLinks]);
    setAmount('');
    setReference('');
    setDescription('');
    setCaptureFields([]);
    setHasExpiry(false);
    setExpiryDate('');
    setExpiryTime('23:59');
  };
  const formatExpiryForDisplay = (expiryString?: string) => {
    if (!expiryString) return 'No expiry';
    const [datePart, timePart] = expiryString.split(' ');
    const date = new Date(datePart + 'T' + timePart);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };
  const isLinkExpired = (expiryString?: string) => {
    if (!expiryString) return false;
    const now = new Date();
    const [datePart, timePart] = expiryString.split(' ');
    const expiryDate = new Date(datePart + 'T' + timePart);
    return now > expiryDate;
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors placeholder:text-gray-400';
  return <div className="space-y-5" data-id="element-7075">
      {/* ─── EXISTING LINKS ─── */}
      {existingLinks.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7076">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7077">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7078">
              <LinkIcon size={16} className="mr-2 text-[#B28B07]" data-id="element-7079" />
              {t('components.FixedLinksPayment.your_fixed_payment_links')}
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-7080">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-7081">
              <thead className="bg-gray-50" data-id="element-7082">
                <tr data-id="element-7083">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7084">
                    {t('components.FixedLinksPayment.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7085">
                    {t('components.BulkPaymentPreview.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7086">
                    {t('components.BulkPaymentList.created')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7087">
                    {t('components.CardOnFilePayment.expires')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7088">
                    {t('components.FixedLinksPayment.usage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7089">
                    {t('components.FixedLinksPayment.total_collected')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7090">
                    {t('components.BulkPaymentList.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7091">
                    {t('components.BulkPaymentList.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-7092">
                {existingLinks.map(link => <tr key={link.id} className={!link.active || isLinkExpired(link.expiryDate) ? 'bg-gray-50' : ''} data-id="element-7093">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-7094">
                      <div className="text-sm font-medium text-gray-900" data-id="element-7095">
                        {link.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-7096">
                        {link.reference}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-7097">
                      {t('components.BulkPaymentListSimplified.usd')}{' '}
                      {link.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-7098">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-7099">
                        <CalendarIcon size={14} className="mr-1.5 text-gray-400" data-id="element-7100" />

                        {link.createdAt}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-7101">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-7102">
                        <ClockIcon size={14} className="mr-1.5 text-gray-400" data-id="element-7103" />
                        {formatExpiryForDisplay(link.expiryDate)}
                        {isLinkExpired(link.expiryDate) && <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded font-medium" data-id="element-7104">
                            {t('components.CardOnFilePayment.expired')}
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-7105">
                      {link.usageCount}{' '}
                      {t('components.FixedLinksPayment.payments')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-7106">
                      {formatCurrencyAmount(link.totalCollected, {
                  fractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-7107">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${link.active && !isLinkExpired(link.expiryDate) ? 'bg-[#FFC70A]/10 text-[#FFC70A]' : 'bg-gray-100 text-gray-800'}`} data-id="element-7108">
                        {link.active && !isLinkExpired(link.expiryDate) ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7109">
                      <div className="flex items-center justify-end space-x-2" data-id="element-7110">
                        <button onClick={() => copyLinkUrl(link.url)} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={t('components.FixedLinksPayment.attrs.copy_link')} data-id="element-7111">
                          <ExternalLinkIcon size={15} data-id="element-7112" />
                        </button>
                        <button onClick={() => handleShareLink(link)} className="p-1.5 rounded-md bg-[#FFC70A]/10 text-[#FFC70A] hover:bg-[#FFC70A]/20 transition-colors" title={t('components.FixedLinksPayment.attrs.share_link')} data-id="element-7113">
                          <ShareIcon size={15} data-id="element-7114" />
                        </button>
                        <button onClick={() => toggleLinkStatus(link.id)} className={`p-1.5 rounded-md transition-colors ${link.active ? 'bg-[#FFC70A]/10 text-[#FFC70A] hover:bg-[#FFC70A]/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title={link.active ? 'Disable link' : 'Enable link'} data-id="element-7115">
                          {link.active ? <ToggleRightIcon size={15} data-id="element-7116" /> : <ToggleLeftIcon size={15} data-id="element-7117" />}
                        </button>
                        <button onClick={() => deleteLink(link.id)} className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title={t('components.FixedLinksPayment.attrs.delete_link_permanently')} data-id="element-7118">
                          <TrashIcon size={15} data-id="element-7119" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {existingLinks.length === 0 && <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-7120">
          <p className="text-gray-500 font-medium" data-id="element-7121">
            {t('components.FixedLinksPayment.you_havent_created_any_fixed_payment')}
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-7122">
            {t('components.FixedLinksPayment.fill_out_the_form_below_to')}
          </p>
        </div>}

      {/* Card 1: Payment details */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7123">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7124">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7125">
            <LinkIcon size={16} className="mr-2 text-[#B28B07]" data-id="element-7126" />
            {t('components.FixedLinksPayment.payment_details')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-7127">
          <div data-id="element-7128">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7129">
              {t('components.CardOnFilePayment.payment_amount')}{' '}
              <span className="text-red-400" data-id="element-7130">*</span>
            </label>
            <div className="relative" data-id="element-7131">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7132">
                <span className="text-sm font-medium text-gray-500" data-id="element-7133">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12 pr-20`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-7134" />

              <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-7135">
                <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-lg focus:ring-[#FFC70A]/20 focus:border-[#FFC70A]" value={currency} onChange={e => handleCurrencyChange(e.target.value)} data-id="element-7136">
                  <option data-id="element-7137">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </option>
                  <option data-id="element-7138">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </option>
                  <option data-id="element-7139">{t('components.FixedLinksPayment.eur')}</option>
                  <option data-id="element-7140">{t('components.FixedLinksPayment.gbp')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7141">
            <div data-id="element-7142">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7143">
                {t('components.FixedLinksPayment.link_reference')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.eg_monthlysubscription_or_donationform')} value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-7144" />
            </div>
            <div data-id="element-7145">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7146">
                {t('components.FixedLinksPayment.payment_description')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.eg_monthly_subscription_or_donation')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-7147" />
            </div>
          </div>

          {/* Expiry toggle */}
          <div data-id="element-7148">
            <div className="flex items-center" data-id="element-7149">
              <label className="inline-flex items-center cursor-pointer" data-id="element-7150">
                <input type="checkbox" className="sr-only" checked={hasExpiry} onChange={() => setHasExpiry(!hasExpiry)} data-id="element-7151" />

                <div className={`w-10 h-5 rounded-full transition-colors ${hasExpiry ? 'bg-[#FFC70A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-7152">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${hasExpiry ? 'translate-x-5' : ''}`} data-id="element-7153" />
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-7154">
                  {t('components.FixedLinksPayment.set_expiration_date_and_time')}
                </span>
              </label>
              <InfoTooltip content="Fixed links don't expire by default. Enable this to set an optional expiration date and time." data-id="element-7155" />
            </div>

            {hasExpiry && <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7156">
                <div className="relative" data-id="element-7157">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7158">
                    {t('components.FixedLinksPayment.expiry_date')}
                  </label>
                  <div className="relative" data-id="element-7159">
                    <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-7160" />

                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7161">
                      <CalendarIcon size={16} className="text-gray-400" data-id="element-7162" />
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-7163">
                      {showDatePicker ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-7164" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-7165" />}
                    </div>
                  </div>

                  {showDatePicker && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-full" data-id="element-7166">
                      <div className="flex justify-between items-center mb-2" data-id="element-7167">
                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-7168">
                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-7169" />
                        </button>
                        <div className="font-medium text-sm" data-id="element-7170">
                          {calendar.month.toLocaleString('default', {
                      month: 'long',
                      year: 'numeric'
                    })}
                        </div>
                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-7171">
                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-7172" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-7173">
                        <div className="text-gray-500" data-id="element-7174">
                          {t('components.FixedLinksPayment.su')}
                        </div>
                        <div className="text-gray-500" data-id="element-7175">
                          {t('components.FixedLinksPayment.mo')}
                        </div>
                        <div className="text-gray-500" data-id="element-7176">
                          {t('components.FixedLinksPayment.tu')}
                        </div>
                        <div className="text-gray-500" data-id="element-7177">
                          {t('components.FixedLinksPayment.we')}
                        </div>
                        <div className="text-gray-500" data-id="element-7178">
                          {t('components.FixedLinksPayment.th')}
                        </div>
                        <div className="text-gray-500" data-id="element-7179">
                          {t('components.FixedLinksPayment.fr')}
                        </div>
                        <div className="text-gray-500" data-id="element-7180">
                          {t('components.FixedLinksPayment.sa')}
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-7181">
                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                  ${isSelected(date) ? 'bg-[#FFC70A] text-white' : ''}
                                  ${isToday(date) && !isSelected(date) ? 'border border-[#FFC70A] text-[#FFC70A]' : ''}
                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                `} onClick={() => handleDateSelect(date)} data-id="element-7182">
                                {date.getDate()}
                              </button> : <div key={index} className="w-8 h-8" data-id="element-7183"></div>))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-7184">
                        <button type="button" className="text-xs font-medium text-[#FFC70A] hover:text-[#E5B309]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 7);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-7185">
                          {t('components.FixedLinksPayment.7_days')}
                        </button>
                        <button type="button" className="text-xs font-medium text-[#FFC70A] hover:text-[#E5B309]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 30);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-7186">
                          {t('components.BulkPaymentPreview.30_days')}
                        </button>
                        <button type="button" className="text-xs font-medium text-[#FFC70A] hover:text-[#E5B309]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 90);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-7187">
                          {t('components.FixedLinksPayment.90_days')}
                        </button>
                      </div>
                    </div>}
                </div>

                <div data-id="element-7188">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7189">
                    {t('components.FixedLinksPayment.expiry_time')}
                  </label>
                  <div className="relative" data-id="element-7190">
                    <input type="time" className={`${inputClass} pl-10`} value={expiryTime} onChange={e => setExpiryTime(e.target.value)} data-id="element-7191" />

                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7192">
                      <ClockIcon size={16} className="text-gray-400" data-id="element-7193" />
                    </div>
                  </div>
                  <div className="mt-1.5 text-xs text-gray-500" data-id="element-7194">
                    {t('components.FixedLinksPayment.time_is_in_your_local_timezone')}
                    {Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Card 2: Customer information */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7195">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7196">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7197">
            <FormInputIcon size={16} className="mr-2 text-[#B28B07]" data-id="element-7198" />
            {t('components.FixedLinksPayment.customer_information')}
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-7199">
            {t('components.FixedLinksPayment.specify_what_information_to_collect_from')}
          </p>
        </div>
        <div className="p-6" data-id="element-7200">
          <CaptureFieldsEditor fields={captureFields} onChange={setCaptureFields} data-id="element-7201" />
        </div>
      </div>

      {/* Card 3: Additional options */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7202">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7203">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7204">
            <SettingsIcon size={16} className="mr-2 text-[#B28B07]" data-id="element-7205" />
            {t('components.CommonPaymentOptions.additional_options')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-7206">
          <div data-id="element-7207">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7208">
              {t('components.CommonPaymentOptions.success_redirect_url')}
            </label>
            <input type="text" className={inputClass} placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} data-id="element-7209" />
          </div>

          <div data-id="element-7210">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7211">
              {t('components.FixedLinksPayment.custom_metadata')}
            </label>
            <button type="button" onClick={() => setMetadata([...metadata, {
            key: '',
            value: ''
          }])} className="inline-flex items-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFC70A]/20 transition-colors" data-id="element-7212">
              <PlusIcon size={16} className="mr-1.5 text-gray-400" data-id="element-7213" />
              {t('components.FixedLinksPayment.add_metadata_field')}
            </button>

            {metadata.length > 0 && <div className="mt-3 space-y-3" data-id="element-7214">
                {metadata.map((item, index) => <div key={index} className="flex space-x-2" data-id="element-7215">
                    <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.key')} value={item.key} onChange={e => {
                const updatedMetadata = [...metadata];
                updatedMetadata[index].key = e.target.value;
                setMetadata(updatedMetadata);
              }} data-id="element-7216" />

                    <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.value')} value={item.value} onChange={e => {
                const updatedMetadata = [...metadata];
                updatedMetadata[index].value = e.target.value;
                setMetadata(updatedMetadata);
              }} data-id="element-7217" />

                    <button type="button" onClick={() => {
                const updatedMetadata = [...metadata];
                updatedMetadata.splice(index, 1);
                setMetadata(updatedMetadata);
              }} className="p-2.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50" data-id="element-7218">
                      <TrashIcon size={16} data-id="element-7219" />
                    </button>
                  </div>)}
              </div>}
          </div>
        </div>
      </div>

      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} recipients={[]} data-id="element-7220" />}
    </div>;
}
// Helper component for the info tooltip
function InfoTooltip({
  content
}: {
  content: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative ml-1.5" data-id="element-7221">
      <div className="text-gray-400 hover:text-gray-500 cursor-help" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-7222">
        <InfoIcon size={14} data-id="element-7223" />
      </div>
      {showTooltip && <div className="absolute z-10 w-64 px-3 py-2 text-sm text-left font-normal text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg -top-2 left-6" data-id="element-7224">
          {content}
        </div>}
    </div>;
}